import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Link, router } from '@inertiajs/react'
import {
  Edit,
  Trash2,
  HelpCircle,
  Eye,
  Calendar,
  MessageCircle
} from 'lucide-react'

interface FaqItemDetail {
  id: number
  question: string
  answer: string
  position: number
  views_count: number
  community_id: number | null
  community_name: string | null
  created_at: string
  updated_at: string
}

interface ShowProps {
  faq_item: FaqItemDetail
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export default function Show({ faq_item }: ShowProps) {
  const handleDelete = () => {
    if (confirm('Voulez-vous vraiment supprimer cette question FAQ ?')) {
      router.delete(`/admin/faq_items/${faq_item.id}`)
    }
  }

  return (
    <AdminLayout
      title="Détail FAQ"
      breadcrumbs={[
        { label: 'FAQ', href: '/admin/faq_items' },
        { label: `Question #${faq_item.id}` }
      ]}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Question Card */}
          <Card className="p-6">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-tessan-cream rounded-xl flex items-center justify-center">
                  <HelpCircle size={28} className="text-tessan-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-text-dark">Question #{faq_item.id}</h2>
                  <p className="text-sm text-text-gray">Position #{faq_item.position}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Link href={`/admin/faq_items/${faq_item.id}/edit`}>
                  <Button variant="secondary" className="flex items-center gap-2">
                    <Edit size={16} />
                    Modifier
                  </Button>
                </Link>
                <Button
                  variant="secondary"
                  className="flex items-center gap-2 text-red-600 hover:bg-red-50"
                  onClick={handleDelete}
                >
                  <Trash2 size={16} />
                  Supprimer
                </Button>
              </div>
            </div>

            {/* Question */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <MessageCircle size={18} className="text-tessan-primary" />
                <h3 className="text-sm font-medium text-text-gray uppercase">Question</h3>
              </div>
              <p className="text-lg text-text-dark font-medium bg-gray-50 p-4 rounded-lg">
                {faq_item.question}
              </p>
            </div>

            {/* Answer */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <HelpCircle size={18} className="text-green-600" />
                <h3 className="text-sm font-medium text-text-gray uppercase">Réponse</h3>
              </div>
              <div className="text-text-dark bg-green-50 p-4 rounded-lg whitespace-pre-wrap">
                {faq_item.answer}
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Stats Card */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-text-dark mb-4">Statistiques</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Eye size={20} className="text-blue-600" />
                  <span className="text-text-dark">Vues</span>
                </div>
                <span className="text-xl font-bold text-blue-600">{faq_item.views_count}</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <HelpCircle size={20} className="text-gray-600" />
                  <span className="text-text-dark">Position</span>
                </div>
                <span className="text-xl font-bold text-text-dark">#{faq_item.position}</span>
              </div>
            </div>
          </Card>

          {/* Info Card */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-text-dark mb-4">Informations</h3>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-text-gray">Communauté</p>
                <p className="text-text-dark font-medium">
                  {faq_item.community_name || <span className="text-text-gray italic">Non assignée</span>}
                </p>
              </div>
              <div className="flex items-center gap-2 text-text-gray">
                <Calendar size={16} />
                <span>Créée le {formatDate(faq_item.created_at)}</span>
              </div>
              <div className="flex items-center gap-2 text-text-gray">
                <Calendar size={16} />
                <span>Modifiée le {formatDate(faq_item.updated_at)}</span>
              </div>
            </div>
          </Card>

          {/* Actions Card */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-text-dark mb-4">Actions</h3>
            <div className="space-y-2">
              <Link href={`/admin/faq_items/${faq_item.id}/edit`} className="block">
                <Button variant="secondary" className="w-full justify-start gap-2">
                  <Edit size={16} />
                  Modifier la question
                </Button>
              </Link>
              <Link href="/admin/faq_items" className="block">
                <Button variant="secondary" className="w-full justify-start gap-2">
                  <HelpCircle size={16} />
                  Retour à la liste
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </AdminLayout>
  )
}
